import { useQuery } from '@apollo/client';
import type { ModerationTopicStats } from '@communities/community/domain';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

import { GET_TOPIC_MODERATION_STATS } from '../graphql';

/**
 * Response shape for topic moderation stats query
 */
interface TopicModerationStatsResponse {
  topicModerationStats: ModerationTopicStats;
}

/**
 * Hook to fetch topic-specific moderation statistics
 * @param communityId - The community ID to fetch stats for
 * @param topic - The topic name to fetch stats for
 * @returns Query result with topic moderation stats
 */
export function useTopicModerationStats(communityId: string, topic: string) {
  const { data, loading, error, refetch } = useQuery<TopicModerationStatsResponse>(GET_TOPIC_MODERATION_STATS, {
    variables: { communityId, topicName: topic },
    skip: !communityId || !topic,
    fetchPolicy: 'cache-and-network',
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  return {
    data,
    loading,
    errors,
    refetch,
  };
}
